import apiClient from "./axiosConfig";

// TeleOpd link + module entitlements (/api/tenants). See docs/teleopd-link-protocol.md.

export type LockSwitch = "off" | "shadow" | "on";
export type TeleopdMode = "ok" | "warning" | "read_only";

export interface LinkAuthFailure {
  at: string;
  reason: string | null;
  ip: string | null;
}

export interface LinkStatusListItem {
  tenantId: string;
  tenantName: string | null;
  linked: boolean;
  enabled: boolean;
  lastSyncAt: string | null;
  syncAgeSeconds: number | null;
  stale: boolean;
  appVersion: string | null;
  lockSwitch: LockSwitch | null;
  readOnlyCapable: boolean;
  lastAuthFailure: LinkAuthFailure | null;
}

export interface ShadowStats {
  since: string | null;
  receivedAt: string | null;
  counts: Array<{ key: string; count: number }>;
}

/** GET /:tenantId/link. Fields after `tenantUri` are present only when `linked` is true. */
export interface TenantLinkStatus {
  tenantId: string;
  tenantName: string | null;
  /** pm2 app name of the deployment. */
  tenantSlug: string | null;
  /** Only "beta" clients can be connected or changed until the production release. */
  tenantStage: "beta" | "locked";
  /** False when SAP_LINK_MASTER_KEY is missing on the SAP server. */
  configured: boolean;
  /** Keys have been issued. */
  linked: boolean;
  enabled: boolean;
  /** Enabled and synced at least once: what every client list calls "connected". */
  connected: boolean;
  baseUrl: string | null;
  baseUrlSource: "link" | "tenant" | null;
  storedBaseUrl: string | null;
  tenantUri: string | null;
  keyId?: string;
  version?: number;
  previousVersion?: number | null;
  previousValidUntil?: string | null;
  previousKeyActive?: boolean;
  acceptedKeyIds?: string[];
  rotatedAt?: string | null;
  lastSeenVersion?: number | null;
  allowPrivateNetwork?: boolean;
  appUri?: string | null;
  appUriMismatch?: boolean;
  appVersion?: string | null;
  lockSwitch?: LockSwitch | null;
  capabilities?: string[];
  readOnlyCapable?: boolean;
  lastSyncAt?: string | null;
  lastSyncIp?: string | null;
  syncAgeSeconds?: number | null;
  stale?: boolean;
  lastAuthFailure?: LinkAuthFailure | null;
  lastSnapshotAt?: string | null;
  snapshotAdopted?: boolean | null;
  shadowStats?: ShadowStats | null;
  rolesSnapshot?: { count: number; fullAccessCount: number; at: string | null };
  localModules?: string[];
  localModulesAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface EnvBlock {
  text: string;
  vars: {
    SAP_BASE_URL: string;
    SAP_KEY_ID: string;
    SAP_KEY_SECRET: string;
    SAP_LOCK: string;
  };
  keyId: string;
  version: number;
  /** False when SAP_PUBLIC_BASE_URL is unset, so SAP_BASE_URL is empty. */
  sapBaseUrlConfigured: boolean;
}

export interface CreateLinkResult {
  created: boolean;
  status: TenantLinkStatus;
  envBlock: EnvBlock;
}

export interface LinkKeysResult {
  status: TenantLinkStatus;
  envBlock: EnvBlock;
}

export interface LinkTestResult {
  ok: boolean;
  latencyMs: number;
  appVersion: string | null;
  lockSwitch: LockSwitch | null;
  mode: TeleopdMode | null;
  snapshotGeneratedAt: number | null;
  baseUrl: string;
}

export interface TeleopdRole {
  id: string;
  name: string;
  fullAccess: boolean;
}

export interface TeleopdRolesResult {
  source: "live" | "snapshot";
  fetchedAt: string | null;
  roles: TeleopdRole[];
  liveError?: { code: string; reason: string; message: string };
}

export interface SyncNowResult {
  delivered: boolean;
  reason?: string;
}

export interface ModuleCatalogEntry {
  name: string;
  label: string;
  group: string;
  permissionKey: string;
}

export interface TenantEntitlements {
  tenantId: string;
  /** Only "beta" clients accept module changes until the production release. */
  stage: "beta" | "locked";
  managed: boolean;
  modules: string[];
  catalog: ModuleCatalogEntry[];
  linked: boolean;
  localModules: string[];
  localModulesAt: string | null;
  updatedAt: string | null;
}

export interface LinkSettingsInput {
  baseUrl?: string | null;
  allowPrivateNetwork?: boolean;
  enabled?: boolean;
}

export const teleopdKeys = {
  all: ["teleopd"] as const,
  statuses: () => ["teleopd", "link-statuses"] as const,
  link: (tenantId: string) => ["teleopd", "link", tenantId] as const,
  roles: (tenantId: string) => ["teleopd", "roles", tenantId] as const,
  catalog: () => ["teleopd", "catalog"] as const,
  entitlements: (tenantId: string) => ["teleopd", "entitlements", tenantId] as const,
};

const BASE = "/api/tenants";

export const fetchLinkStatuses = async (): Promise<LinkStatusListItem[]> => {
  const res = await apiClient.get<LinkStatusListItem[]>(`${BASE}/link-statuses`);
  return res.data;
};

export const fetchModuleCatalog = async (): Promise<ModuleCatalogEntry[]> => {
  const res = await apiClient.get<ModuleCatalogEntry[]>(`${BASE}/modules/catalog`);
  return res.data;
};

export const fetchTenantLink = async (tenantId: string): Promise<TenantLinkStatus> => {
  const res = await apiClient.get<TenantLinkStatus>(`${BASE}/${tenantId}/link`);
  return res.data;
};

export const connectTenantLink = async (
  tenantId: string,
  input: { baseUrl?: string | null; allowPrivateNetwork?: boolean }
): Promise<CreateLinkResult> => {
  const res = await apiClient.post<CreateLinkResult>(`${BASE}/${tenantId}/link`, input);
  return res.data;
};

export const revealTenantLinkKeys = async (tenantId: string): Promise<LinkKeysResult> => {
  const res = await apiClient.post<LinkKeysResult>(`${BASE}/${tenantId}/link/reveal`);
  return res.data;
};

export const rotateTenantLinkKeys = async (
  tenantId: string,
  previousValidDays?: number
): Promise<LinkKeysResult> => {
  const res = await apiClient.post<LinkKeysResult>(
    `${BASE}/${tenantId}/link/rotate`,
    previousValidDays ? { previousValidDays } : {}
  );
  return res.data;
};

export const updateTenantLink = async (
  tenantId: string,
  input: LinkSettingsInput
): Promise<TenantLinkStatus> => {
  const res = await apiClient.patch<TenantLinkStatus>(`${BASE}/${tenantId}/link`, input);
  return res.data;
};

export const testTenantLink = async (tenantId: string): Promise<LinkTestResult> => {
  const res = await apiClient.post<LinkTestResult>(`${BASE}/${tenantId}/link/test`);
  return res.data;
};

export const fetchTeleopdRoles = async (tenantId: string): Promise<TeleopdRolesResult> => {
  const res = await apiClient.get<TeleopdRolesResult>(`${BASE}/${tenantId}/link/roles`);
  return res.data;
};

export const syncTenantNow = async (tenantId: string): Promise<SyncNowResult> => {
  const res = await apiClient.post<SyncNowResult>(`${BASE}/${tenantId}/link/sync-now`);
  return res.data;
};

export const fetchEntitlements = async (tenantId: string): Promise<TenantEntitlements> => {
  const res = await apiClient.get<TenantEntitlements>(`${BASE}/${tenantId}/entitlements`);
  return res.data;
};

export const updateEntitlements = async (
  tenantId: string,
  input: { managed: boolean; modules: string[] }
): Promise<TenantEntitlements> => {
  const res = await apiClient.put<TenantEntitlements>(`${BASE}/${tenantId}/entitlements`, input);
  return res.data;
};
